<template>
  <div>
    <Navigation />

    <div class="change-password-container">
      <h2>Change Password</h2>
      <p class="subtitle">
        Please enter your current password to change password
      </p>

      <form class="form" @submit.prevent="savePasswordChanges">
        <div class="form-group">
          <label>Current Password</label>
          <div class="password-wrapper">
            <input
              :type="showCurrentPassword ? 'text' : 'password'"
              v-model="currentPassword"
              placeholder="Enter current password"
              :disabled="!isEditing"
              autocomplete="current-password"
            />
            <button
              type="button"
              class="eye-btn"
              :aria-pressed="showCurrentPassword"
              @click="toggleShow('current')"
              :disabled="!isEditing"
              title="Show / hide current password"
            >
              <i
                :class="showCurrentPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
              ></i>
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Enter New Password</label>
          <div class="password-wrapper">
            <input
              :type="showNewPassword ? 'text' : 'password'"
              v-model="newPassword"
              placeholder="Enter new password"
              :disabled="!isEditing"
              autocomplete="new-password"
            />
            <button
              type="button"
              class="eye-btn"
              :aria-pressed="showNewPassword"
              @click="toggleShow('new')"
              :disabled="!isEditing"
              title="Show / hide new password"
            >
              <i :class="showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>

          <p class="hint" v-if="isEditing && newPassword">
            <span :class="passwordStrengthClass">{{
              passwordStrengthLabel
            }}</span>
          </p>
        </div>

        <div class="form-group">
          <label>Re-enter New Password</label>
          <div class="password-wrapper">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="confirmPassword"
              placeholder="Confirm new password"
              :disabled="!isEditing"
              autocomplete="new-password"
            />
            <button
              type="button"
              class="eye-btn"
              :aria-pressed="showConfirmPassword"
              @click="toggleShow('confirm')"
              :disabled="!isEditing"
              title="Show / hide confirm password"
            >
              <i
                :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
              ></i>
            </button>
          </div>

          <p class="hint" v-if="isEditing && confirmPassword">
            <span v-if="passwordsMatch" class="match">Passwords match ✓</span>
            <span v-else class="nomatch">Passwords do not match ✕</span>
          </p>
        </div>

        <!-- BUTTONS: single container -->
        <div class="button-group">
          <button
            v-if="!isEditing"
            type="button"
            class="change-btn"
            @click="startEdit"
          >
            Edit
          </button>

          <template v-else>
            <button type="button" class="cancel-btn" @click="cancelEdit">
              Cancel
            </button>
            <button type="submit" class="change-btn">Save</button>
          </template>
        </div>
      </form>
    </div>

    <Footer />
  </div>
</template>

<script>
import axios from "axios";
import "../../../public/css/main.css";
import Navigation from "./Navigation.vue";
import Footer from "./footer.vue";
import { toast } from "vue-sonner";

export default {
  name: "DropdownPasswordview",
  components: { Navigation, Footer },

  data() {
    return {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      isEditing: false,

      // visibility flags
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    };
  },

  computed: {
    passwordsMatch() {
      return this.newPassword && this.newPassword === this.confirmPassword;
    },

    passwordStrengthLabel() {
      const p = this.newPassword || "";
      if (
        p.length >= 12 &&
        /[A-Z]/.test(p) &&
        /\d/.test(p) &&
        /[^A-Za-z0-9]/.test(p)
      ) {
        return "Strong";
      } else if (p.length >= 8 && (/[A-Z]/.test(p) || /\d/.test(p))) {
        return "Medium";
      } else {
        return "Weak";
      }
    },

    passwordStrengthClass() {
      const label = this.passwordStrengthLabel;
      return label === "Strong"
        ? "strength-strong"
        : label === "Medium"
        ? "strength-medium"
        : "strength-weak";
    },
  },

  methods: {
    startEdit() {
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.clearAll();
    },

    clearAll() {
      this.currentPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
    },

    toggleShow(field) {
      if (!this.isEditing) return;

      if (field === "current")
        this.showCurrentPassword = !this.showCurrentPassword;
      else if (field === "new") this.showNewPassword = !this.showNewPassword;
      else if (field === "confirm")
        this.showConfirmPassword = !this.showConfirmPassword;
    },

    async savePasswordChanges() {
      if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
        toast.error("All fields are required ❗");
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        toast.error("Passwords do not match ❌");
        this.newPassword = "";
        this.confirmPassword = "";
        return;
      }

      const userId = localStorage.getItem("userid");
      if (!userId) {
        toast.error("User not found. Please login again.");
        return;
      }

      try {
        // Verify current password server-side (the stored password is never
        // sent to the browser).
        const verifyRes = await axios.post("/api/verify-password", {
          userID: userId,
          password: this.currentPassword,
        });

        if (!verifyRes.data?.valid) {
          toast.error("Current password is incorrect 😕");
          this.currentPassword = "";
          return;
        }

        // update password
        await axios.put("/api/updateoneuser", {
          userID: userId,
          password: this.confirmPassword,
        });

        toast.success("Password changed successfully 🎉");
        this.isEditing = false;
        this.clearAll();
      } catch (error) {
        console.error("Error updating password:", error);
        toast.error("Something went wrong. Please try again ❌");
      }
    },
  },
};
</script>

<style scoped>
.change-password-container {
  width: 100%;
  max-width: 646px;
  margin: 50px auto;
  background: #fff;
  border-radius: 16px;
  padding: 32px 40px;
  font-family: "Inter", sans-serif;
  box-sizing: border-box;
}

h2 {
  font-size: 24px;
  font-weight: 600;
  color: #181d27;
  line-height: 32px;
  margin: 0 0 8px 0;
}

.subtitle {
  color: #535862;
  font-size: 14px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
}

label {
  font-size: 14px;
  color: #414651;
  margin-bottom: 6px;
}

.password-wrapper {
  position: relative;
  display: block;
}

.password-wrapper input {
  width: 100%;
  padding: 10px 42px 10px 14px;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.eye-btn {
  position: absolute;
  right: 10px;
  top: 40%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.eye-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.eye-btn:focus {
  outline: 2px solid rgba(52, 103, 213, 0.2);
}

.hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #556;
}
.match {
  color: #16a34a;
  font-weight: 600;
}
.nomatch {
  color: #dc2626;
  font-weight: 600;
}
.strength-weak {
  color: #dc2626;
  font-weight: 600;
}
.strength-medium {
  color: #f59e0b;
  font-weight: 600;
}
.strength-strong {
  color: #16a34a;
  font-weight: 600;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.cancel-btn {
  background: transparent;
  border: 1px solid #d5d7da;
  color: #374151;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}
.cancel-btn:hover {
  background: #f8fafc;
}

.change-btn {
  background: #3467d5;
  color: #ffffff;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}
.change-btn:hover {
  background: #1e40af;
}

input:disabled {
  background: #f2f4f7;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .change-password-container {
    padding: 20px;
  }
}
</style>
